import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { BUSINESS } from "@/lib/content";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/products", label: "Products" },
  { to: "/services", label: "Services" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const darkHero = ["/", "/about", "/services"].includes(location.pathname);
  const lightText = !scrolled && darkHero;

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
        scrolled ? "backdrop-blur-2xl bg-brand-bone/80 border-b border-brand-forest/10" : "bg-transparent"
      }`}
    >
      <nav className="max-w-screen-2xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex flex-col leading-none">
          <span className={`font-display text-2xl md:text-[26px] tracking-tight transition-colors duration-300 ${lightText ? "text-white" : "text-brand-forest"}`}>
            Pravin Hiralal Prajapati
          </span>
          <span className={`font-mono text-[9px] md:text-[10px] uppercase tracking-[0.28em] transition-colors duration-300 ${lightText ? "text-brand-leaf-light" : "text-brand-leaf"}`}>
            Wholesale Vegetables · Est. Karnavati
          </span>
        </Link>

        <div className="hidden lg:flex items-center gap-9">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `relative text-sm font-medium transition-colors duration-300 ${
                  lightText
                    ? isActive ? "text-white" : "text-white/70 hover:text-white"
                    : isActive ? "text-brand-forest" : "text-brand-muted hover:text-brand-forest"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {l.label}
                  {isActive && (
                    <motion.span layoutId="nav-underline" className="absolute -bottom-1.5 left-0 right-0 h-[2px] bg-brand-terra" />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </div>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href={`tel:${BUSINESS.phoneRaw}`}
            data-testid="nav-call-btn"
            className={`inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-medium transition-colors duration-300 active:scale-95 ${
              lightText ? "bg-white text-brand-forest hover:bg-brand-sand" : "bg-brand-forest text-brand-bone hover:bg-brand-forest-light"
            }`}
          >
            <Phone size={15} /> Call Now
          </a>
        </div>

        <button
          data-testid="nav-menu-toggle"
          onClick={() => setOpen((o) => !o)}
          className={`lg:hidden p-2 transition-colors duration-300 ${lightText ? "text-white" : "text-brand-forest"}`}
          aria-label="Toggle menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.76, 0, 0.24, 1] }}
            className="lg:hidden overflow-hidden bg-brand-bone backdrop-blur-2xl border-b border-brand-forest/10"
          >
            <div className="px-6 py-6 flex flex-col gap-1">
              {LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                  className="font-display text-3xl text-brand-forest py-2 border-b border-brand-forest/5"
                >
                  {l.label}
                </NavLink>
              ))}
              <a
                href={`tel:${BUSINESS.phoneRaw}`}
                className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-brand-forest text-brand-bone px-6 py-4 font-medium"
              >
                <Phone size={16} /> Call Now
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
